import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  CircularProgress,
  Chip,
  IconButton,
  alpha,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ClearIcon from '@mui/icons-material/Clear';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import AuthenticatedImage from '../common/AuthenticatedImage';
import { assetsApi } from '../../services/api';

/**
 * DisplayImageSelector Component
 *
 * A dialog component that allows users to select a display image for an asset
 * from available inspection photos.
 *
 * Props:
 *  - open (boolean): Controls dialog visibility
 *  - onClose (function): Called when dialog is closed
 *  - asset (object): The asset to set display image for
 *  - currentPhotoId (number|null): Currently selected photo ID
 *  - onSelect (function): Called when a photo is selected (receives photoId or null)
 */
const DisplayImageSelector = ({
  open,
  onClose,
  asset,
  currentPhotoId,
  onSelect,
}) => {
  const theme = useTheme();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPhotoId, setSelectedPhotoId] = useState(currentPhotoId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && asset?.id) {
      fetchPhotos();
      setSelectedPhotoId(currentPhotoId);
      setError(null);
    }
  }, [open, asset?.id, currentPhotoId]);

  const fetchPhotos = async () => {
    setLoading(true);
    try {
      const response = await assetsApi.getAvailableDisplayImages(asset.id);
      setPhotos(response.data || []);
    } catch (err) {
      console.error('Failed to fetch available photos:', err);
      setError('Failed to load photos');
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (selectedPhotoId === currentPhotoId) {
      onClose();
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await assetsApi.setDisplayImage(asset.id, selectedPhotoId);
      onSelect(selectedPhotoId);
      onClose();
    } catch (err) {
      console.error('Failed to set display image:', err);
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    setSaving(true);
    setError(null);
    try {
      await assetsApi.clearDisplayImage(asset.id);
      onSelect(null);
      onClose();
    } catch (err) {
      console.error('Failed to clear display image:', err);
      setError('Failed to clear. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Group photos by inspection
  const groupedPhotos = photos.reduce((acc, photo) => {
    const key = `${photo.inspection_id}-${photo.inspection_date}`;
    if (!acc[key]) {
      acc[key] = {
        inspection_id: photo.inspection_id,
        inspection_date: photo.inspection_date,
        inspection_type: photo.inspection_type_name,
        photos: [],
      };
    }
    acc[key].photos.push(photo);
    return acc;
  }, {});

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown Date';
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '85vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PhotoLibraryIcon color="primary" />
          <Typography variant="h6">Select Display Image</Typography>
        </Box>
        <IconButton onClick={onClose} size="small" disabled={saving}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2 }}>
        {error && (
          <Box
            sx={{
              p: 2,
              mb: 2,
              bgcolor: alpha(theme.palette.error.main, 0.1),
              borderRadius: 1,
              border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
            }}
          >
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : photos.length === 0 ? (
          <Box
            sx={{
              py: 6,
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <PhotoLibraryIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
            <Typography color="text.secondary">
              No inspection photos available for this asset.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Add inspection photos first to select a display image.
            </Typography>
          </Box>
        ) : (
          Object.values(groupedPhotos).map((group) => (
            <Box key={group.inspection_id} sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 1.5,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {group.inspection_type || 'Inspection'}
                </Typography>
                <Chip
                  label={formatDate(group.inspection_date)}
                  size="small"
                  variant="outlined"
                  sx={{ height: 24 }}
                />
                <Typography variant="caption" color="text.secondary">
                  ({group.photos.length} photo{group.photos.length !== 1 ? 's' : ''})
                </Typography>
              </Box>

              <Grid container spacing={1}>
                {group.photos.map((photo) => (
                  <Grid item xs={4} sm={3} md={2} key={photo.id}>
                    <Box
                      onClick={() => !saving && setSelectedPhotoId(photo.id)}
                      sx={{
                        position: 'relative',
                        aspectRatio: '4/3',
                        borderRadius: 1,
                        overflow: 'hidden',
                        cursor: saving ? 'not-allowed' : 'pointer',
                        border:
                          selectedPhotoId === photo.id
                            ? `3px solid ${theme.palette.primary.main}`
                            : '3px solid transparent',
                        opacity: saving ? 0.7 : 1,
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          opacity: saving ? 0.7 : 0.85,
                          transform: saving ? 'none' : 'scale(1.02)',
                        },
                      }}
                    >
                      <AuthenticatedImage
                        src={photo.photo_path}
                        alt={photo.caption || 'Inspection photo'}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                      {selectedPhotoId === photo.id && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            bgcolor: theme.palette.primary.main,
                            borderRadius: '50%',
                            p: 0.25,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <CheckCircleIcon sx={{ fontSize: 18, color: 'white' }} />
                        </Box>
                      )}
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Button
          startIcon={<ClearIcon />}
          onClick={handleClear}
          disabled={saving || !currentPhotoId}
          color="inherit"
          sx={{ opacity: currentPhotoId ? 1 : 0.5 }}
        >
          Use Default
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || selectedPhotoId === currentPhotoId || photos.length === 0}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default DisplayImageSelector;
